import { Component, ElementRef, EventEmitter, Input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { getCurrentTimeOfTimezone } from '@app/helpers/timezone-handler';
import { CustomCalendarDateRange, CustomCalendarDateRangeOption, DateRangeValues } from '@app/models';
import { Timezone } from '@app/models/organization.model';
import { MOMENT_DATE_FORMAT, DATE_TIME_FORMATS } from '@app/shared/data/values';
import * as _moment from 'moment';

const moment = _moment;
type ActiveField = 'start' | 'end';

@Component({
  selector: 'app-custom-date-picker',
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.scss'],
  providers: [
    {
        provide: DateAdapter,
        useClass: MomentDateAdapter,
        deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MOMENT_DATE_FORMAT }
  ],
  standalone: false
})
export class CustomDatePickerComponent implements OnInit {
  
  @Input() timezone!: Timezone;
  @Input('disabled') disabled: boolean = false;
  @Output() dateChange: EventEmitter<CustomCalendarDateRange | null> = new EventEmitter<CustomCalendarDateRange | null>();
  @ViewChild('datePickerTriggerButton') datePickerTriggerButton!: MatMenuTrigger;
  @ViewChild('startInput') startInput!: ElementRef<HTMLInputElement>;
  @ViewChild('endInput') endInput!: ElementRef<HTMLInputElement>;
  
  activeField = signal<ActiveField>('start'); // Tracks which input is focused
  calendarRangeForm = new FormGroup({
    start: new FormControl<moment.Moment | null>(null),
    end: new FormControl<moment.Moment | null>(null)
  });
  dateTimeFormats = DATE_TIME_FORMATS;
  selectedType: string | null = null;
  selectedValue: CustomCalendarDateRange | null = null;
  currentTimeOfTz!: moment.Moment;
  currentDate!: moment.Moment;
  predefinedOptions: {[range: string]: CustomCalendarDateRange | null} = {};
  dateRangeValues = DateRangeValues;
  dateRangeOptions: CustomCalendarDateRangeOption[] = [
    { label: 'All Time', value: this.dateRangeValues.allTime },
    { label: 'This Month', value: this.dateRangeValues.month },
    { label: 'Last Month', value: this.dateRangeValues.lastMonth },
    { label: 'This Week', value: this.dateRangeValues.thisWeek },
    { label: 'Last Week', value: this.dateRangeValues.lastWeek },
    { label: 'Last 30 Days', value: this.dateRangeValues.last30Days }
  ];

  constructor() {}

  ngOnInit(): void {
    this.currentTimeOfTz = getCurrentTimeOfTimezone(this.timezone.timezone_identifier);
    this.currentDate = this.currentTimeOfTz.startOf('day');
    
    this.predefinedOptions = {
      [this.dateRangeValues.month]: {
        start: this.currentTimeOfTz.clone().startOf('month'),
        end: moment.min(this.currentTimeOfTz.clone().endOf('month'), this.currentDate.clone().endOf('day'))
      },
      [this.dateRangeValues.lastMonth]: {
        start: this.currentTimeOfTz.clone().subtract(1, 'month').startOf('month'),
        end: this.currentTimeOfTz.clone().subtract(1, 'month').endOf('month')
      },
      [this.dateRangeValues.thisWeek]: {
        start: this.currentTimeOfTz.clone().startOf('week'),
        end: moment.min(this.currentTimeOfTz.clone().endOf('week'), this.currentDate.clone().endOf('day'))
      },
      [this.dateRangeValues.lastWeek]: {
        start: this.currentTimeOfTz.clone().subtract(1, 'week').startOf('week'),
        end: this.currentTimeOfTz.clone().subtract(1, 'week').endOf('week')
      },
      [this.dateRangeValues.last30Days]: {
        start: this.currentTimeOfTz.clone().subtract(29, 'days').startOf('day'),
        end: this.currentTimeOfTz.clone().endOf('day')
      }, 
      [this.dateRangeValues.allTime]: null
    };
    this.selectedType = 'allTime';
  }

  onFilterSelected(type: string) {
    if (this.selectedType === type) return;

    const filteredDateRange: CustomCalendarDateRange | null = this.predefinedOptions[type] ?? null;

    if (filteredDateRange) {
      this.selectedValue = {...filteredDateRange};
      this.selectedType = type;
      this.resetForm();
    } else {
      this.selectedType = 'allTime';
    }
    this.emitValue();
  }

  openDatePicker() {
    this.selectedValue ??= { 
        start: this.currentDate.clone().startOf('day'), 
        end: this.currentDate.clone().startOf('day') 
      };
    this.resetForm();
    this.selectedType = null;
    this.activeField.set('start');
    this.datePickerTriggerButton.openMenu();
    setTimeout(() => this.startInput.nativeElement.focus(), 0);
  }
  
  apply() {
    this.datePickerTriggerButton.closeMenu();
    this.selectedValue = { ...this.formValues };
    this.emitValue();
  }

  cancel() {
    this.datePickerTriggerButton.closeMenu();
    this.resetForm();
  }

  resetForm() {
    this.calendarRangeForm.reset({
      start: this.selectedValue?.start,
      end: this.selectedValue?.end
    });
  }

  emitValue() {
    this.dateChange.emit(this.selectedValue ? {...this.selectedValue} : null);
  }

  // Called when user clicks inside an input
  onInputFocus(field: ActiveField) {
    this.activeField.set(field);
  }

  // provide current view to calendar
  get startAtDate(): moment.Moment {
    const field = this.activeField();
    const currentValue = this.formValues[field];

    // Navigate to value if exists
    if (currentValue) {
      return currentValue;
    }

    // If on End field (empty), try navigating to Start date
    if (field === 'end') {
      const startDate = this.formValues.start;
      if (startDate) return startDate;
    }

    // Fallback to Today in timezone
    return this.currentDate;
  }

  get currentSelectedDate(): moment.Moment | null {
    return this.formValues[this.activeField()] ?? null;
  }

  onDateSelected(date: moment.Moment | null): void {
    if (!date) return;

    if (this.activeField() === 'start') {
      this.calendarRangeForm.get('start')?.setValue(date);

      // Validate End Date against new Start Date
      const currentEnd = this.formValues.end;
      if (currentEnd?.isBefore(date)) {
        this.calendarRangeForm.get('end')?.setValue(null);
      }

    } else {
      this.calendarRangeForm.get('end')?.setValue(date);
    }
  }

  // Helper to disable dates in End selection that are before Start date
  get minDate(): moment.Moment | null {
    if (this.activeField() === 'end') {
      return this.formValues.start;
    }
    return null;
  }

  get formValues() {
    return this.calendarRangeForm.value as CustomCalendarDateRange;
  }

  get filterDropDownLabel() {
    const start = this.formValues.start?.format(this.dateTimeFormats.fullMonthShortDateFullYear) ?? '';
    const end = this.formValues.end?.format(this.dateTimeFormats.fullMonthShortDateFullYear) ?? '';
    return `${start} - ${end}`;
  }
  
  /**
   * Restrict User from writing own in the input field.
   * @returns False
   */
  restrictInput(): boolean {
      return false;
  }

}